import type { ErrorEvent, EventHint } from '@sentry/react';
import { stage } from '../firebase/config';

/**
 * Errors caused by the user's browser, network or extensions rather than our
 * code. Sentry tests each pattern against the exception value and against
 * `${type}: ${value}`, so every pattern is anchored to a full message.
 */
export const sentryIgnoreErrors: RegExp[] = [
  // A tab from an older deploy requests a chunk the newer deploy removed.
  /^Failed to fetch dynamically imported module: /,
  /^error loading dynamically imported module: /,
  /^Importing a module script failed\.$/,
  /^'text\/html' is not a valid JavaScript MIME type\.$/,
  // Firebase Auth SDK race when an OAuth popup is closed or opened twice.
  /^INTERNAL ASSERTION FAILED: Pending promise was never set$/,
  // Offline, or a revoked session that Firebase Auth signs out of itself.
  /^Firebase: Error \(auth\/network-request-failed\)\.$/,
  /^Firebase: Error \(auth\/user-token-expired\)\.$/,
  // IndexedDB failures in the browser's storage layer (Firebase persistence).
  /^UnknownError: Internal error\.$/,
  /^UnknownError: Internal error opening backing store for indexedDB\.open\.$/,
  /^AbortError: AbortError$/,
  /^AbortError: The transaction was aborted, so the request cannot be fulfilled\.$/,
  // Requests we abort on unmount, rejected by a fetch wrapper we don't own.
  /^AbortError: signal is aborted without reason$/,
];

/** Scripts injected into the page by browser extensions. */
const injectedScriptUrls = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-(web-)?extension:\/\//,
  /\/card\/card-injection\.js/,
];

/** Errors thrown from these scripts are dropped (Sentry checks the top frame). */
export const sentryDenyUrls = injectedScriptUrls;

const networkErrorMessages = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

const injectedRejectionMessages = [/^wallet must has at least one account$/];

const getMessage = (value: unknown): unknown =>
  value && typeof value === 'object' && 'message' in value
    ? value.message
    : undefined;

/** `beforeSend` filter for noise that `ignoreErrors` and `denyUrls` can't match. */
export function shouldDropSentryEvent(
  event: ErrorEvent,
  hint?: EventHint,
): boolean {
  const exceptions = event.exception?.values ?? [];
  const frames = exceptions.flatMap((exception) =>
    exception.stacktrace?.frames ? exception.stacktrace.frames : [],
  );

  // A failed fetch with no stack, or one wrapped by an extension, is the
  // client's network or extension: our own fetches catch their failures.
  const lastException = exceptions[exceptions.length - 1];
  if (
    lastException?.type === 'TypeError' &&
    networkErrorMessages.has(lastException.value ?? '') &&
    (frames.length === 0 ||
      frames.some((frame) =>
        injectedScriptUrls.some((pattern) =>
          pattern.test(frame.filename ?? ''),
        ),
      ))
  ) {
    return true;
  }

  // Crypto wallet extensions reject with plain objects; we have no wallet code.
  const rejection =
    getMessage(hint?.originalException) ??
    getMessage(event.extra?.['__serialized__']);
  return (
    typeof rejection === 'string' &&
    injectedRejectionMessages.some((pattern) => pattern.test(rejection))
  );
}

/** Only the production build served from a real host reports to Sentry. */
export function shouldReportToSentry(
  hostname = window.location.hostname,
): boolean {
  return (
    process.env.NODE_ENV === 'production' &&
    stage === 'prod' &&
    !['localhost', '127.0.0.1', '[::1]'].includes(hostname)
  );
}
